import { useState } from 'react'
import { Search } from '@/assets/icons'
import { CitingCasesAccordion } from './citator_tab_views/CitingCasesAccordion'
import { CitatorCasesAccordion } from './citator_tab_views/CitatorCasesAccordion'
import { useGetCitatorCaseCitations } from '../hooks/useGetCitatorCaseCitations'
import { useGetCitatorCitingCases } from '../hooks/useGetCitatorCitingCases'
import { useGetCitatorRelatedCases } from '../hooks/useGetCitatorRelatedCases'
import { RiaCitatorSection } from '@/analytics/enums/ria.enums'
import { RiaSourceContext } from '@/analytics/utils/ria-source-analytics'
import { CitatorSurface } from '@/analytics/enums/citator.enums'
import clsx from 'clsx'

export type CaseLawReferenceCitatorTabProps = {
  metadata?: Record<string, unknown>
  parentId?: string
  analyticsContext?: RiaSourceContext | null
  /** Analytics surface; omit to skip Citator outcome events (e.g. standalone viewer). */
  citatorSurface?: CitatorSurface
  /** When set, open this accordion section on mount (e.g. Case Citations for overruling risk). */
  defaultOpenSection?: RiaCitatorSection
  className?: string
}

/**
 * Citation analysis tab body. Renders the global filter at the top and three accordions
 * (citing cases, case citations, related cases). Citing cases is default-open and stretches
 * to fill available height; the others sit below and open on demand.
 */
export default function CaseLawReferenceCitatorTab(props: CaseLawReferenceCitatorTabProps) {
  const { metadata, parentId, analyticsContext, citatorSurface, defaultOpenSection, className } = props
  const parent_id = parentId ?? (typeof metadata?.parent_id === 'string' ? metadata.parent_id : '')

  const [filterQuery, setFilterQuery] = useState('')

  const {
    data: caseCitationsData,
    isLoading: caseCitationsIsLoading,
    isError: caseCitationsIsError,
    refetch: caseCitationsRefetch,
  } = useGetCitatorCaseCitations(parent_id)

  const {
    data: citingCasesData,
    isLoading: citingCasesIsLoading,
    isError: citingCasesIsError,
    refetch: citingCasesRefetch,
  } = useGetCitatorCitingCases(parent_id)

  const {
    data: relatedCasesData,
    isLoading: relatedCasesIsLoading,
    isError: relatedCasesIsError,
    refetch: relatedCasesRefetch,
  } = useGetCitatorRelatedCases(parent_id)

  return (
    <div className={clsx('flex flex-col gap-4 h-full min-h-0 px-3 pt-2 pb-4 overflow-y-auto', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-neutral-500" />
        <input
          className="w-full rounded-md border border-brand-neutral-200 bg-white pl-9 pr-3 py-2 text-sm text-brand-neutral-900 placeholder:text-brand-neutral-500 focus:outline-hidden focus:ring-2 focus:ring-brand-500"
          id="citator-filter"
          name="citator-filter"
          placeholder="Filter by name..."
          type="text"
          value={filterQuery}
          onChange={(event) => setFilterQuery(event.target.value)}
        />
      </div>

      <CitingCasesAccordion
        data={citingCasesData}
        isLoading={citingCasesIsLoading}
        isError={citingCasesIsError}
        refetch={citingCasesRefetch}
        filterQuery={filterQuery}
        analyticsContext={analyticsContext}
        citatorSurface={citatorSurface}
      />

      <CitatorCasesAccordion
        title="Case citations"
        section={RiaCitatorSection.CaseCitations}
        data={caseCitationsData}
        isLoading={caseCitationsIsLoading}
        isError={caseCitationsIsError}
        refetch={caseCitationsRefetch}
        filterQuery={filterQuery}
        analyticsContext={analyticsContext}
        defaultOpen={defaultOpenSection === RiaCitatorSection.CaseCitations}
      />

      <CitatorCasesAccordion
        title="Related cases"
        section={RiaCitatorSection.RelatedCases}
        data={relatedCasesData}
        isLoading={relatedCasesIsLoading}
        isError={relatedCasesIsError}
        refetch={relatedCasesRefetch}
        filterQuery={filterQuery}
        analyticsContext={analyticsContext}
        defaultOpen={defaultOpenSection === RiaCitatorSection.RelatedCases}
      />
    </div>
  )
}
